import { useState } from 'react'
import {
  alturaDoTerrenoEm,
  analisarVFixa,
  comprimentoDoV,
  equilibrioVEqualizado,
  GRAVIDADE_M_S2,
  localizarPontoNoMundo,
  parametrosDoObjeto,
  type AncoragemTabletop,
  type CenaTabletop,
  type ObjetoTabletop,
} from '../../dominio/tabletop'
import type { ModoSimulacaoV } from '../../fisica/simulacaoAncoragem'
import { PARAMETROS_ESCADA_PADRAO, useSimulacoes, type ParametrosSimulacaoEscada } from './simulacoes'

const virgula = (valor: number, casas = 1) => valor.toFixed(casas).replace('.', ',')
const kgf = (newtons: number) => newtons / GRAVIDADE_M_S2

function EstadoDoMotor() {
  const { estadoMotor } = useSimulacoes()
  if (estadoMotor === 'carregando') return <p className="dica" role="status">Carregando o motor de física…</p>
  if (estadoMotor === 'indisponivel') {
    return (
      <p className="aviso-corda" role="alert">
        O motor de física não carregou. A mesa continua funcionando pelo desenho e pelo cálculo; só a simulação fica indisponível.
      </p>
    )
  }
  return null
}

const AVISO_DIDATICO = 'Simulação didática: mostra o comportamento, não vale como ensaio nem como capacidade de equipamento.'

// ---------------------------------------------------------------------------
// Escada apoiada
// ---------------------------------------------------------------------------

function CampoNumero({
  rotulo,
  valor,
  min,
  max,
  passo,
  aoMudar,
}: {
  rotulo: string
  valor: number
  min: number
  max: number
  passo: number
  aoMudar: (valor: number) => void
}) {
  return (
    <label className="campo">
      <span>{rotulo}</span>
      <input
        type="number"
        aria-label={rotulo}
        min={min}
        max={max}
        step={passo}
        value={valor}
        onChange={(e) => {
          const numero = Number(e.target.value)
          if (e.target.value !== '' && Number.isFinite(numero)) aoMudar(Math.min(max, Math.max(min, numero)))
        }}
      />
    </label>
  )
}

/**
 * Teste de estabilidade da escada, no inspetor da escada: apoiada no topo numa
 * parede lisa, com atrito só no pé. Mostra o que a fórmula diz e o que a
 * simulação faz, lado a lado.
 */
export function SimulacaoEscada({ objeto }: { objeto: ObjetoTabletop }) {
  const { escadas, simularEscada, estadoMotor } = useSimulacoes()
  const [p, setP] = useState<ParametrosSimulacaoEscada>(PARAMETROS_ESCADA_PADRAO)
  const medidas = parametrosDoObjeto(objeto)
  const resultado = escadas[objeto.id]
  const noLimite = resultado !== undefined && Math.abs(resultado.anguloGraus - resultado.analitico.anguloMinimoGraus) <= 1
  const desatualizado =
    resultado !== undefined && (resultado.anguloGraus !== medidas.anguloGraus || resultado.comprimentoM !== medidas.comprimentoM)

  return (
    <details className="simulacao-fisica">
      <summary>Estabilidade da escada (simulação)</summary>
      <p className="dica">
        Modelo: escada apoiada no topo numa parede lisa, com atrito só no pé. O chão sob o pé é o terreno da cena (talude, borda
        de barranco). Não representa a escada usada como mastro com estais.
      </p>
      <CampoNumero rotulo="Atrito do pé (μ)" valor={p.atritoPe} min={0} max={1.5} passo={0.05} aoMudar={(atritoPe) => setP({ ...p, atritoPe })} />
      <CampoNumero rotulo="Massa da escada (kg)" valor={p.massaEscadaKg} min={1} max={60} passo={1} aoMudar={(massaEscadaKg) => setP({ ...p, massaEscadaKg })} />
      <CampoNumero rotulo="Massa da carga (kg)" valor={p.massaCargaKg} min={0} max={400} passo={5} aoMudar={(massaCargaKg) => setP({ ...p, massaCargaKg })} />
      <CampoNumero
        rotulo="Posição da carga (% do pé ao topo)"
        valor={Math.round(p.posicaoCargaFracao * 100)}
        min={0}
        max={100}
        passo={5}
        aoMudar={(porcento) => setP({ ...p, posicaoCargaFracao: porcento / 100 })}
      />
      <div className="acoes-linha">
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--destaque"
          disabled={estadoMotor === 'carregando'}
          onClick={() => void simularEscada(objeto, p)}
        >
          Testar estabilidade
        </button>
      </div>
      <EstadoDoMotor />
      {resultado && (
        <div aria-live="polite">
          {desatualizado && (
            <p className="aviso-corda" role="alert">
              A escada mudou (ângulo ou comprimento) depois do teste: teste de novo.
            </p>
          )}
          {resultado.simulado.deitadaNoChao && (
            <p className="aviso-corda" role="alert">
              A escada está mais deitada que o chão sob o pé ({virgula(resultado.inclinacaoChaoGraus, 0)}°): ela ficaria enterrada no
              terreno. Levante a escada ou mude o apoio e teste de novo.
            </p>
          )}
          <p className="corda-resumo">
            <strong>{resultado.simulado.deitadaNoChao ? 'Não se aplica' : resultado.simulado.escorregou ? 'Escorrega' : 'Fica estável'}</strong>
            <span className="dica">
              {' '}
              a {virgula(resultado.anguloGraus, 0)}°
              {resultado.simulado.escorregou ? ` (o pé andou ${virgula(Math.abs(resultado.simulado.deslocamentoPeM), 2)} m)` : ''}
            </span>
          </p>
          <ul className="lista-pontos-ancoragem">
            <li>
              Chão sob o pé:{' '}
              {Math.abs(resultado.inclinacaoChaoGraus) < 0.5
                ? 'plano'
                : `${virgula(Math.abs(resultado.inclinacaoChaoGraus), 0)}° ${resultado.inclinacaoChaoGraus > 0 ? 'subindo para a parede (pé no ponto baixo)' : 'descendo para a parede'}`}
            </li>
            <li>Ângulo mínimo pela fórmula: {virgula(resultado.analitico.anguloMinimoGraus)}°</li>
            <li>
              Atrito necessário no pé: {virgula(kgf(resultado.analitico.atritoNecessarioN))} kgf de {virgula(kgf(resultado.analitico.atritoDisponivelN))} kgf disponíveis
            </li>
            <li>Reação da parede no topo: {virgula(kgf(resultado.analitico.reacaoParedeN))} kgf</li>
          </ul>
          {noLimite ? (
            <p className="dica">
              A escada está no limite de estabilidade (a menos de 1° do ângulo mínimo): um pouco mais de ângulo ou de atrito a deixa estável, e um pouco menos a faz escorregar.
            </p>
          ) : (
            resultado.simulado.escorregou === resultado.analitico.estavel && (
              <p className="aviso-corda" role="alert">
                A simulação e a fórmula discordam nesta escada. Trate o resultado com cautela.
              </p>
            )
          )}
        </div>
      )}
      <p className="dica">{AVISO_DIDATICO}</p>
    </details>
  )
}

// ---------------------------------------------------------------------------
// Ancoragem em V
// ---------------------------------------------------------------------------

/** O que a fórmula diz para o mesmo V que se simula, em N por perna; `null` se algum ponto não existe mais. */
function tensoesPelaFormula(cena: CenaTabletop, a: AncoragemTabletop, modo: ModoSimulacaoV, rigidezNPorM: number): readonly [number, number] | null {
  const pontos = [...a.fixacoes, a.centro].map((r) => localizarPontoNoMundo(cena, r))
  if (pontos.some((p) => p === null)) return null
  const [fixacaoA, fixacaoB, centro] = pontos as [NonNullable<(typeof pontos)[0]>, NonNullable<(typeof pontos)[0]>, NonNullable<(typeof pontos)[0]>]
  if (modo === 'fixa') {
    const r = analisarVFixa(centro, fixacaoA, fixacaoB, a.cargaKgf)
    return [r.tensoesN[0], r.tensoesN[1]]
  }
  const r = equilibrioVEqualizado(fixacaoA, fixacaoB, comprimentoDoV(centro, fixacaoA, fixacaoB), a.cargaKgf, rigidezNPorM)
  return [r.tensaoN, r.tensaoN]
}

/**
 * Simulação do V no inspetor da ancoragem: pernas fixas ou equalizada, com a
 * opção de uma fixação ceder, e a reprodução do movimento na cena.
 */
export function SimulacaoAncoragemV({ cena, ancoragem }: { cena: CenaTabletop; ancoragem: AncoragemTabletop }) {
  const s = useSimulacoes()
  const [modo, setModo] = useState<ModoSimulacaoV>('equalizada')
  const [cede, setCede] = useState<'nenhuma' | '0' | '1'>('nenhuma')
  const minha = s.ancoragem && s.ancoragem.idAncoragem === ancoragem.id ? s.ancoragem : null
  const quadros = minha?.resultado.quadros ?? []
  const quadro = minha ? quadros[Math.min(s.indiceQuadro, quadros.length - 1)] : null
  const formula = minha ? tensoesPelaFormula(cena, ancoragem, minha.modo, minha.resultado.rigidezNPorM) : null
  const cedeuNoFim = minha?.cedeIndice !== null && minha?.cedeIndice !== undefined
  // a simulação não tem chão: se o anel assenta abaixo dele, a montagem não faria isso de verdade
  const assentado = minha?.resultado.assentado.centro
  const abaixoDoChaoM = assentado ? alturaDoTerrenoEm(cena, assentado[0], assentado[2]) - assentado[1] : 0

  return (
    <details className="simulacao-fisica">
      <summary>Simulação do V</summary>
      <fieldset className="opcoes-simulacao" role="radiogroup" aria-label="Modo da ancoragem">
        <legend>Como as pernas são montadas</legend>
        <label className="opcao-simulacao">
          <input type="radio" name="modo-v" checked={modo === 'equalizada'} onChange={() => { setModo('equalizada'); setCede('nenhuma') }} /> Equalizada (uma corda passa pelas duas fixações; o anel desliza)
        </label>
        <label className="opcao-simulacao">
          <input type="radio" name="modo-v" checked={modo === 'fixa'} onChange={() => setModo('fixa')} /> Pernas fixas (cada perna é uma corda própria)
        </label>
      </fieldset>
      {modo === 'fixa' && (
        <label className="campo">
          <span>Uma fixação cede</span>
          <select aria-label="Fixação que cede" value={cede} onChange={(e) => setCede(e.target.value as typeof cede)}>
            <option value="nenhuma">Nenhuma</option>
            <option value="0">Fixação 1</option>
            <option value="1">Fixação 2</option>
          </select>
        </label>
      )}
      <div className="acoes-linha">
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--destaque"
          disabled={s.estadoMotor === 'carregando'}
          onClick={() => void s.simularAncoragem(ancoragem, modo, cede === 'nenhuma' ? null : (Number(cede) as 0 | 1))}
        >
          Simular
        </button>
        {minha && (
          <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={s.limparAncoragem}>
            Limpar simulação
          </button>
        )}
      </div>
      <EstadoDoMotor />

      {minha && quadro && (
        <div aria-live="polite">
          <div className="campo">
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={s.alternarReproducao}>
              {s.tocando ? 'Pausar' : 'Reproduzir'}
            </button>
            <input
              type="range"
              aria-label="Tempo da simulação"
              min={0}
              max={quadros.length - 1}
              value={Math.min(s.indiceQuadro, quadros.length - 1)}
              onChange={(e) => s.definirIndiceQuadro(Number(e.target.value))}
            />
            <span className="dica">{virgula(quadro.t)} s</span>
          </div>
          <ul className="lista-pontos-ancoragem">
            <li>
              Tensão por perna agora: {virgula(kgf(quadro.tensoesN[0]), 0)} kgf e {virgula(kgf(quadro.tensoesN[1]), 0)} kgf
            </li>
            <li>
              Ao assentar: {virgula(kgf(minha.resultado.assentado.tensoesN[0]), 0)} kgf e {virgula(kgf(minha.resultado.assentado.tensoesN[1]), 0)} kgf
              {minha.resultado.assentou ? '' : ' (ainda se movia ao fim da simulação)'}
            </li>
            {formula && !cedeuNoFim && (
              <li>
                Pela fórmula: {virgula(kgf(formula[0]), 0)} kgf e {virgula(kgf(formula[1]), 0)} kgf
              </li>
            )}
            {cedeuNoFim && (
              <li>
                Uma fixação cedeu aos 1,0 s: a carga cai, balança e a perna que ficou segura o peso todo ({virgula(ancoragem.cargaKgf, 0)} kgf).
              </li>
            )}
          </ul>
          {abaixoDoChaoM > 0.01 && (
            <p className="aviso-corda" role="alert">
              A carga assentou {virgula(abaixoDoChaoM * 100, 0)} cm abaixo do chão: as fixações estão baixas demais para este V. A simulação não leva em conta o chão, então esta montagem não se comportaria assim.
            </p>
          )}
          <p className="dica">
            {minha.modo === 'equalizada'
              ? 'No V equalizado o anel desliza até o ponto mais baixo e as duas pernas ficam com a mesma tensão, mesmo com fixações em alturas diferentes.'
              : 'Com as pernas fixas o ponto central quase não se move e a perna mais vertical segura mais.'}
          </p>
        </div>
      )}
      <p className="dica">
        Só o V é simulado: no W e no M não há uma corda única que equalize três ou quatro pernas. {AVISO_DIDATICO}
      </p>
    </details>
  )
}
